import type { RemoteProjectUpload } from './projectUploadBridge'

/**
 * Server-side deletion notice (bulk delete, single delete, cart cleanup).
 * Published by api UploadEventPublisher::publishUploadsDeleted() on the same
 * topic as regular upload events, distinguished by `event: 'deleted'`.
 */
export type UploadDeletedEvent = {
  event: 'deleted'
  ids: string[]
  projectCode: string
  categoryId: number
  deletedAt?: string
}

/** Render-Status eines Design-Dokuments (Backend UploadEventPublisher::publishDesignRenderEvent). */
export type DesignRenderEvent = {
  event: 'render:done' | 'render:failed' | 'render:pending'
  documentId: string
  kind?: string
  renderId?: string
  warnings?: any[]
  error?: string
}

export function normalizeDesignRenderEvent(payload: any): DesignRenderEvent | null {
  if (!payload || typeof payload !== 'object') return null
  const event = String(payload.event || '')
  if (event !== 'render:done' && event !== 'render:failed' && event !== 'render:pending') return null
  const documentId = String(payload.documentId || '').trim()
  if (!documentId) return null
  return {
    event,
    documentId,
    kind: payload.kind ? String(payload.kind) : undefined,
    renderId: payload.renderId ? String(payload.renderId) : undefined,
    warnings: Array.isArray(payload.warnings) ? payload.warnings : undefined,
    error: payload.error ? String(payload.error) : undefined,
  }
}

export function normalizeUploadDeletedEvent(payload: any): UploadDeletedEvent | null {
  if (!payload || typeof payload !== 'object') return null
  if (String(payload.event || '').trim() !== 'deleted') return null
  const ids = Array.isArray(payload.ids)
    ? [...new Set(payload.ids.map((id: unknown) => String(id ?? '').trim()).filter(Boolean))] as string[]
    : []
  if (!ids.length) return null
  return {
    event: 'deleted',
    ids,
    projectCode: String(payload.projectCode || '').trim(),
    categoryId: Math.max(0, Number(payload.categoryId || 0)),
    deletedAt: payload.deletedAt ? String(payload.deletedAt) : undefined,
  }
}

export function buildUploadEventTopic(editorBaseUrl: string, projectCode: string, categoryId: number) {
  const root = String(editorBaseUrl || '').trim().replace(/\/+$/, '').replace(/\/editor$/i, '')
  const code = encodeURIComponent(String(projectCode || '').trim())
  const category = Math.max(0, Number(categoryId || 0))
  return `${root}/editor/upload-events/${code}/${category}`
}

export function resolveMercureHubUrl(editorBaseUrl: string, configured?: string | null) {
  const explicit = String(configured || '').trim()
  if (explicit) {
    try {
      return typeof window === 'undefined'
        ? explicit
        : new URL(explicit, window.location.origin).toString()
    } catch {
      return explicit
    }
  }

  const base = String(editorBaseUrl || '').trim().replace(/\/+$/, '')
  if (!base) return ''

  try {
    const root = new URL(base, typeof window !== 'undefined' ? window.location.origin : undefined)
    root.pathname = root.pathname.replace(/\/editor$/i, '') || '/'
    return new URL('.well-known/mercure', root.toString().replace(/\/+$/, '/') ).toString()
  } catch {
    const root = base.replace(/\/editor$/i, '')
    return root ? `${root}/.well-known/mercure` : ''
  }
}

export function subscribeToUploadEvents(opts: {
  editorBaseUrl: string
  mercureHubUrl?: string | null
  projectCode: string
  categoryId: number | number[]
  onUpload: (item: RemoteProjectUpload) => void | Promise<void>
  /** Called instead of onUpload when the server announces removed uploads. */
  onDeleted?: (event: UploadDeletedEvent) => void | Promise<void>
  /** Design-Editor: Render-Status (render:done/failed/pending) statt onUpload. */
  onRenderEvent?: (event: DesignRenderEvent) => void | Promise<void>
  onError?: () => void
}) {
  if (typeof window === 'undefined' || typeof EventSource === 'undefined') {
    return null
  }

  const hubUrl = resolveMercureHubUrl(opts.editorBaseUrl, opts.mercureHubUrl)
  if (!hubUrl) return null

  const url = new URL(hubUrl)
  const categoryIds = Array.isArray(opts.categoryId) ? opts.categoryId : [opts.categoryId]
  const uniqueCategoryIds = [...new Set(categoryIds.map((id) => Math.max(0, Number(id || 0))))]
  for (const categoryId of uniqueCategoryIds) {
    url.searchParams.append('topic', buildUploadEventTopic(opts.editorBaseUrl, opts.projectCode, categoryId))
  }

  const es = new EventSource(url.toString(), { withCredentials: false })

  es.onmessage = (evt) => {
    // Note: the server currently emits unnamed SSE messages, so onmessage is
    // sufficient. If named events are introduced, add es.addEventListener(name, ...)
    // handlers here as well.
    try {
      const payload = JSON.parse(String(evt.data || '{}'))
      if (!payload || typeof payload !== 'object') return
      // Deletion notices share the topic but must never be treated as an
      // upload patch (they carry no preview/status fields).
      const renderEvent = normalizeDesignRenderEvent(payload)
      if (renderEvent) {
        if (opts.onRenderEvent) {
          void Promise.resolve(opts.onRenderEvent(renderEvent)).catch((err) => {
            console.warn('[uploadEvents] onRenderEvent handler rejected', err)
          })
        }
        return
      }
      const deleted = normalizeUploadDeletedEvent(payload)
      if (deleted) {
        if (opts.onDeleted) {
          void Promise.resolve(opts.onDeleted(deleted)).catch((err) => {
            console.warn('[uploadEvents] onDeleted handler rejected', err)
          })
        }
        return
      }
      // onUpload may be async; swallow/log handler rejections so they don't
      // surface as unhandled promise rejections.
      void Promise.resolve(opts.onUpload(payload as RemoteProjectUpload)).catch((err) => {
        console.warn('[uploadEvents] onUpload handler rejected', err)
      })
    } catch {
      // ignore malformed events
    }
  }

  es.onerror = () => {
    // EventSource auto-reconnects on transient errors, but if the caller forgets
    // to dispose this subscription a persistent error would loop forever. Close
    // the connection to stop the infinite reconnect loop and notify the caller,
    // who can decide whether to re-subscribe.
    try { es.close() } catch {}
    opts.onError?.()
  }

  return () => {
    try { es.close() } catch {}
  }
}
